import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import {
  ArrowLeft,
  Robot,
  CheckCircle,
  Warning,
  XCircle,
  HourglassMedium,
  ChartBar,
  CircleNotch,
  ArrowsClockwise,
  Pulse,
  Funnel,
} from "@phosphor-icons/react";

const FLOWS = [
  { key: "ride_status", label: "Ride Status", color: "#10b981" },
  { key: "cab_booking", label: "Cab Booking", color: "#6366f1" },
  { key: "missed_call", label: "Missed Call", color: "#f59e0b" },
  { key: "pause_bypass", label: "Pause Bypass", color: "#ec4899" },
  { key: "routing", label: "Routing Audit", color: "#a855f7" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "2-digit", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 19);
  }
}

function StatCard({ label, value, hint, tone = "default", testid }) {
  const toneClass = {
    default: "border-zinc-700 bg-zinc-900/40",
    ok: "border-emerald-500/40 bg-emerald-500/10",
    warn: "border-amber-500/40 bg-amber-500/10",
    fail: "border-rose-500/40 bg-rose-500/10",
    info: "border-indigo-500/40 bg-indigo-500/10",
    pink: "border-pink-500/40 bg-pink-500/10",
  }[tone] || "border-zinc-700 bg-zinc-900/40";
  return (
    <div data-testid={testid} className={`border ${toneClass} rounded-xl p-4 flex flex-col gap-1`}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}

const DECISION_COLORS = {
  // ride_status
  status_card: "emerald", no_match_ack: "amber", send_failed: "rose",
  // cab_booking
  menu_shown: "indigo", pickup_set: "sky", drop_set: "cyan",
  cab_selected: "violet", datetime_set: "fuchsia",
  booked: "emerald", book_failed: "rose", abandoned: "zinc",
  // missed_call
  yes_help_me: "emerald", issue_resolved: "amber",
  // routing audit — "fired_*" green, "silent_*" amber/rose
  fired_keyword: "emerald", fired_fresh_conv: "emerald",
  fired_status_query: "sky", fired_restart: "indigo", fired_step: "violet",
  silent_no_match: "zinc", silent_paused: "amber",
  silent_blocked: "rose", silent_system_event: "zinc",
  silent_status_only: "amber",
};

function DecisionBadge({ decision }) {
  const color = DECISION_COLORS[decision] || "zinc";
  const label = (decision || "?").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-${color}-500/15 text-${color}-300 border border-${color}-500/30 capitalize`}>
      {label}
    </span>
  );
}

function ApiBadge({ status }) {
  if (status === "ok") return <span className="text-emerald-300">API ok</span>;
  if (status === "empty") return <span className="text-amber-300">API empty</span>;
  if (status === "exception") return <span className="text-rose-300">API error</span>;
  if (status === "n/a" || !status) return <span className="text-zinc-500">—</span>;
  return <span className="text-zinc-400">{status}</span>;
}

function FunnelChart({ funnel, color = "#6366f1" }) {
  if (!funnel || funnel.length === 0) return null;
  // Funnel viz — horizontal bars, width proportional to count vs first step.
  const top = Math.max(1, ...funnel.map((f) => f.count || 0));
  return (
    <div className="space-y-2" data-testid="funnel-chart">
      {funnel.map((step) => {
        const pct = step.count ? (step.count / top) * 100 : 0;
        return (
          <div key={step.key} className="flex items-center gap-2 text-[12px]">
            <div className="w-32 shrink-0 text-zinc-400 capitalize truncate">
              {step.label}
            </div>
            <div className="flex-1 h-7 bg-zinc-800/60 rounded relative overflow-hidden">
              <div
                className="h-full rounded transition-all"
                style={{ width: `${Math.max(pct, step.count ? 4 : 0)}%`, background: color }}
              />
              <div className="absolute inset-0 flex items-center px-2 text-white font-mono">
                <span data-testid={`funnel-${step.key}-count`}>{step.count}</span>
                {step.conversion_pct != null && (
                  <span className="ml-2 text-[10px] text-white/70">
                    ({step.conversion_pct}% from prev)
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BotHealth() {
  const navigate = useNavigate();
  const [flow, setFlow] = useState("ride_status");
  const [hours, setHours] = useState(24);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [bypassStats, setBypassStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, b] = await Promise.all([
        api.get(`/admin/bot-health/stats?hours=${hours}&flow=${flow}`),
        api.get(`/admin/bot-health/events?hours=${hours}&flow=${flow}&decision=${filter}&limit=200`),
        api.get(`/admin/bot-health/stats?hours=${hours}&flow=pause_bypass`),
      ]);
      setStats(s.data);
      setItems(Array.isArray(l.data?.items) ? l.data.items : []);
      setBypassStats(b.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load bot health");
    } finally {
      setLoading(false);
    }
  }, [hours, filter, flow]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const t = setInterval(loadAll, 30_000);
    return () => clearInterval(t);
  }, [loadAll]);

  // Reset filter when flow changes (decision keys differ per flow)
  useEffect(() => {
    setFilter("all");
  }, [flow]);

  const daily = (stats?.daily || []).filter(
    (d) => d && typeof d.day === "string" && d.day.length >= 10
  );
  const dailyChart = daily.map((d) => ({ day: d.day.slice(5), Total: d.total || 0 }));

  const flowMeta = FLOWS.find((f) => f.key === flow) || FLOWS[0];

  // Card setup per flow
  const cardConfig = {
    ride_status: [
      { label: "Total queries", value: stats?.total ?? "—", tone: "info", testid: "stat-total" },
      { label: "Status cards sent", value: stats?.by_decision?.status_card ?? 0, tone: "ok", hint: `${stats?.api_health?.hit_rate_pct ?? 0}% API hit rate` },
      { label: "No-match acks", value: stats?.by_decision?.no_match_ack ?? 0, tone: "warn" },
      { label: "Send failures", value: stats?.by_decision?.send_failed ?? 0, tone: stats?.by_decision?.send_failed ? "fail" : "default" },
    ],
    cab_booking: [
      { label: "Menus shown", value: stats?.by_decision?.menu_shown ?? 0, tone: "info", testid: "stat-total" },
      { label: "Bookings completed", value: stats?.by_decision?.booked ?? 0, tone: "ok" },
      { label: "Booking failures", value: stats?.by_decision?.book_failed ?? 0, tone: stats?.by_decision?.book_failed ? "fail" : "default" },
      {
        label: "Conversion %",
        value: (() => {
          const m = stats?.by_decision?.menu_shown || 0;
          const b = stats?.by_decision?.booked || 0;
          return m ? `${Math.round((b / m) * 100)}%` : "—";
        })(),
        hint: "Booked / Menu",
        tone: "info",
      },
    ],
    missed_call: [
      { label: "Yes, help me", value: stats?.by_decision?.yes_help_me ?? 0, tone: "ok", testid: "stat-total" },
      { label: "Issue resolved", value: stats?.by_decision?.issue_resolved ?? 0, tone: "warn" },
      { label: "Total taps", value: stats?.total ?? 0, tone: "info" },
      {
        label: "Help-conversion %",
        value: (() => {
          const t = stats?.total || 0;
          const y = stats?.by_decision?.yes_help_me || 0;
          return t ? `${Math.round((y / t) * 100)}%` : "—";
        })(),
        hint: "Yes-help / Total",
        tone: "info",
      },
    ],
  };
  const cards = cardConfig[flow] || cardConfig.ride_status;

  // Filter chip options per flow (decisions that can appear)
  const filterOpts = [{ k: "all", label: "All" }];
  (stats?.funnel || []).forEach((f) => filterOpts.push({ k: f.key, label: f.label }));
  if (flow === "ride_status") filterOpts.push({ k: "send_failed", label: "Send failed" });
  if (flow === "cab_booking") filterOpts.push({ k: "book_failed", label: "Book failed" });

  return (
    <div data-testid="bot-health-page" className="min-h-screen bg-[#0b0d18] text-zinc-200 p-6 font-sans">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> Back to inbox
          </button>
          <div className="flex items-center gap-2">
            <div className="text-[11px] text-zinc-500 hidden md:block">Auto-refreshes every 30s · Live</div>
            <button data-testid="refresh-btn" onClick={loadAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs">
              <ArrowsClockwise size={13} /> Refresh
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/15 border border-indigo-500/30">
              <Robot size={20} className="text-indigo-300" weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Bot Health</h1>
              <p className="text-[12px] text-zinc-400">
                Funnel & monitoring · last {hours === 1 ? "1 hour" : `${hours} hours`} · flow:{" "}
                <span style={{ color: flowMeta.color }}>{flowMeta.label}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-zinc-900/50 border border-zinc-700 rounded-lg p-0.5">
            {[1, 24, 168, 720].map((h) => (
              <button
                key={h} data-testid={`window-${h}h`} onClick={() => setHours(h)}
                className={`px-2.5 py-1 rounded text-[11px] font-medium transition ${hours === h ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                {h === 1 ? "1h" : h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
              </button>
            ))}
          </div>
        </div>

        {/* Flow tabs */}
        <div className="flex items-center gap-1 bg-zinc-900/50 border border-zinc-700 rounded-lg p-1 w-fit" data-testid="flow-tabs">
          {FLOWS.map((f) => (
            <button
              key={f.key} data-testid={`flow-tab-${f.key}`} onClick={() => setFlow(f.key)}
              className={`px-3 py-1.5 rounded text-[12px] font-medium transition ${flow === f.key ? "text-white" : "text-zinc-400 hover:text-white"}`}
              style={flow === f.key ? { background: f.color } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stat-cards">
          {cards.map((c, i) => <StatCard key={i} {...c} />)}
        </div>

        {/* Funnel viz */}
        {stats?.funnel && stats.funnel.length > 0 && (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4" data-testid="funnel-card">
            <div className="flex items-center gap-2 mb-3">
              <Funnel size={16} className="text-zinc-400" />
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Funnel — {flowMeta.label}</div>
            </div>
            <FunnelChart funnel={stats.funnel} color={flowMeta.color} />
          </div>
        )}

        {/* Pause-bypass widget — visible across all tabs. Counts how
            often a customer button click rescued a paused conversation
            and let the bot continue (engagement signal). */}
        {bypassStats && (
          <div
            className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-4"
            data-testid="pause-bypass-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <Funnel size={16} className="text-pink-400" />
              <div className="text-[11px] uppercase tracking-wider text-pink-300">
                Pause-bypass events
              </div>
              <div className="ml-auto text-[10px] text-zinc-500">
                Last {hours}h · button clicks that auto-resumed the bot
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                label="Total bypasses"
                value={String(bypassStats.total ?? 0)}
                hint="customer button clicks while paused"
                tone="pink"
                testid="bypass-total"
              />
              <StatCard
                label="Visual bot resumed"
                value={String(
                  (bypassStats.by_decision?.auto_resume ?? 0)
                )}
                hint="paused → resumed"
                testid="bypass-visual"
              />
              <StatCard
                label="Active pauses"
                value={String(bypassStats.active_pauses ?? 0)}
                hint="rodbez paused right now"
                tone={bypassStats.active_pauses > 0 ? "warn" : "default"}
                testid="bypass-active"
              />
              <StatCard
                label="Last bypass"
                value={
                  bypassStats.last_query_at
                    ? fmtTime(bypassStats.last_query_at)
                    : "—"
                }
                hint={bypassStats.last_query_phone || "no events yet"}
                testid="bypass-last"
              />
            </div>
          </div>
        )}

        {dailyChart.length > 0 && (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4" data-testid="daily-chart">
            <div className="flex items-center gap-2 mb-3">
              <ChartBar size={16} className="text-zinc-400" />
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Daily volume</div>
            </div>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="day" stroke="#a1a1aa" fontSize={11} />
                  <YAxis stroke="#a1a1aa" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Total" fill={flowMeta.color} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-zinc-900/50 border border-zinc-700 rounded-lg p-0.5 flex-wrap">
            {filterOpts.map((opt) => (
              <button
                key={opt.k} data-testid={`filter-${opt.k}`} onClick={() => setFilter(opt.k)}
                className={`px-2.5 py-1 rounded text-[11px] font-medium ${filter === opt.k ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 flex items-center gap-1">
            <Pulse size={12} /> {items.length} of {stats?.total ?? 0} events
          </div>
        </div>

        <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 overflow-hidden" data-testid="events-table">
          <div className="px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-400 grid grid-cols-12 gap-2">
            <div className="col-span-3">Time</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Decision</div>
            <div className="col-span-1">API</div>
            <div className="col-span-2">Ride / Booking</div>
            <div className="col-span-2 text-right">Note</div>
          </div>
          {loading && (
            <div className="px-4 py-12 flex items-center justify-center text-sm text-zinc-500 gap-2">
              <CircleNotch size={14} className="animate-spin" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-zinc-500">
              <HourglassMedium size={20} className="inline mb-2 text-zinc-600" />
              <div>No bot decisions for this flow + window yet.</div>
              <div className="text-[11px] mt-1">Customer interactions will appear here in real-time.</div>
            </div>
          )}
          {!loading && items.map((row, idx) => {
            const r = row || {};
            const key = r.wamid || `row-${idx}-${r.ts || ""}`;
            return (
              <div key={key} data-testid={`event-row-${idx}`} className="px-4 py-3 border-b border-zinc-800/60 grid grid-cols-12 gap-2 text-sm hover:bg-zinc-800/30">
                <div className="col-span-3 text-zinc-300">{fmtTime(r.ts)}</div>
                <div className="col-span-2 text-zinc-200 font-mono text-[12px]">+{r.phone || "—"}</div>
                <div className="col-span-2"><DecisionBadge decision={r.decision} /></div>
                <div className="col-span-1 text-[11px]"><ApiBadge status={r.api_status} /></div>
                <div className="col-span-2 text-[11px] text-zinc-300">
                  {r.booking_no ? (
                    <div>
                      <div className="font-mono">{r.booking_no}</div>
                      {r.ride_status && <div className="text-zinc-500">{r.ride_status}</div>}
                    </div>
                  ) : r.ride_id ? (
                    <div className="font-mono text-amber-300">{r.ride_id}</div>
                  ) : r.extra ? (
                    <div className="text-zinc-500 truncate">
                      {Object.entries(r.extra).slice(0, 2).map(([k, v]) => (
                        <div key={k}>{k}: {v}</div>
                      ))}
                    </div>
                  ) : <span className="text-zinc-600">—</span>}
                </div>
                <div className="col-span-2 text-right text-[11px] text-zinc-400 truncate" title={r.error || r.text}>
                  {r.error ? <span className="text-rose-400">{r.error}</span> : r.text || "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BotHealth;
